import type { DashboardListData } from "@lifter/hub-ui/browser-shared";
import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useDashboardsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.dashboards(),
    queryFn: () => api.dashboards.list(),
    enabled: options.enabled ?? true,
  });
}

export function useUpdateDashboardVisibilityMutation(dashboardId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visibility: "agent" | "private") =>
      api.dashboards.updateVisibility(dashboardId, visibility),
    onSuccess: (data) => {
      queryClient.setQueryData<DashboardListData>(hubQueryKeys.dashboards(), (current) =>
        current
          ? {
              ...current,
              dashboards: current.dashboards.map((dashboard) =>
                dashboard.dashboardId === dashboardId
                  ? { ...dashboard, visibility: data.visibility }
                  : dashboard,
              ),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.dashboard(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.dashboards(), exact: true });
    },
  });
}

export function useDeleteDashboardMutation(
  dashboardId: string,
  feedback: { onError?(error: unknown): void; onSuccess?(): void } = {},
) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.dashboards.delete(dashboardId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: hubQueryKeys.dashboard(dashboardId) });
      queryClient.setQueryData<DashboardListData>(hubQueryKeys.dashboards(), (current) =>
        current
          ? {
              ...current,
              dashboards: current.dashboards.filter(
                (dashboard) => dashboard.dashboardId !== dashboardId,
              ),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.dashboards(), exact: true });
      feedback.onSuccess?.();
    },
    onError: feedback.onError,
  });
}
